import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Plus, Trash2, Loader2, Users, Tag, Calendar, Briefcase, Archive, RotateCcw, Download, Upload, UserPlus, Pencil, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import InviteUserDialog from "@/components/InviteUserDialog";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { useConfirm } from "@/components/ConfirmDialog";

export default function AdminPage() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="px-6 md:px-10 pt-6 max-w-6xl mx-auto" data-testid="admin-page">
      <div className="flex items-center gap-2 mb-6">
        <ShieldCheck className="h-6 w-6 text-emerald-600" />
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Admin</h1>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="company" className="rounded-lg" data-testid="admin-tab-company">
            <Briefcase className="h-4 w-4 mr-1.5" /> Company
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-lg" data-testid="admin-tab-users">
            <Users className="h-4 w-4 mr-1.5" /> Users
          </TabsTrigger>
          <TabsTrigger value="work" className="rounded-lg" data-testid="admin-tab-work">
            <Tag className="h-4 w-4 mr-1.5" /> Work
          </TabsTrigger>
          <TabsTrigger value="periods" className="rounded-lg" data-testid="admin-tab-periods">
            <Calendar className="h-4 w-4 mr-1.5" /> Pay Periods
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company" className="mt-4"><CompanyTab /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="work" className="mt-4"><WorkTab /></TabsContent>
        <TabsContent value="periods" className="mt-4"><PeriodsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function CompanyTab() {
  const [name, setName] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/settings");
    setName(r.data?.company_name || "");
    setLoaded(true);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/settings", { company_name: name });
      toast.success("Company name updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <div className="text-sm text-slate-400 p-4">Loading…</div>;

  return (
    <div className="tg-card p-6 max-w-xl" data-testid="company-tab">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Company name</div>
      <p className="text-xs text-slate-500 mb-3">Shown in the header for everyone in this workspace, and on printed timesheets.</p>
      <div className="flex gap-2">
        <input
          className="tg-input flex-1"
          value={name}
          placeholder="e.g. Immunab Research"
          onChange={(e) => setName(e.target.value)}
          data-testid="company-name-input"
        />
        <button onClick={save} disabled={busy} className="tg-btn-primary disabled:opacity-50" data-testid="company-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
        </button>
      </div>
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const { active: activeCompany, activeId: activeCompanyId } = useCompany();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState({});
  const [positions, setPositions] = useState([]);

  const startEdit = (u, rowKey) => {
    setEditingKey(rowKey);
    setDraft({
      role: u.role || "employee",
      supervisor_id: u.supervisor_id || "",
      work_start_date: u.work_start_date || "",
      employee_id_number: u.employee_id_number || "",
      position: u.position || "",
    });
  };
  const cancelEdit = () => { setEditingKey(null); setDraft({}); };
  const saveEdit = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, draft);
      toast.success("Saved");
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/users");
    setUsers(r.data);
    setLoading(false);
  };
  useEffect(() => { api.get("/positions").then((r) => setPositions(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, []);

  const archive = async (u) => {
    const ok = await confirm({
      title: `Archive ${u.name || u.email}?`,
      description: "They lose access to this workspace until restored. Their other companies (if any) are not affected.",
      confirmText: "Archive",
    });
    if (!ok) return;
    try {
      await api.patch(`/admin/users/${u.id}/archive`);
      toast.success("User archived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const unarchive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}/unarchive`);
      toast.success("User restored");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cancelInvite = async (u) => {
    const ok = await confirm({
      title: `Cancel invite for ${u.email}?`,
      description: "They won't be able to join via the existing link.",
      confirmText: "Cancel invite",
      danger: true,
    });
    if (!ok || !u.membership_id || !activeCompanyId) return;
    try {
      await api.delete(`/companies/${activeCompanyId}/members/${u.membership_id}`);
      toast.success("Invite cancelled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (u) => {
    const ok = await confirm({
      title: `Remove ${u.name || u.email}?`,
      description: "They lose access to this workspace. Their account & other workspaces are not affected.",
      confirmText: "Remove from workspace",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-sm text-slate-400 p-4">Loading…</div>;
  const filtered = showArchived ? users : users.filter((u) => (u.membership_status || "active") !== "archived");
  const canInvite = !!activeCompanyId && activeCompanyId !== "__all__";

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-xs text-slate-500">{filtered.length} users · <button onClick={() => setShowArchived((v) => !v)} className="text-emerald-600 font-semibold hover:underline" data-testid="toggle-archived">{showArchived ? "Hide archived" : "Show archived"}</button></div>
        <button
          onClick={() => setInviteOpen(true)}
          disabled={!canInvite}
          className="tg-btn-primary disabled:opacity-50"
          data-testid="invite-user-btn"
          title={canInvite ? "" : "Switch to a specific company to invite users"}
        >
          <UserPlus className="h-4 w-4" /> Invite user
        </button>
      </div>
      <InviteUserDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        companyName={activeCompany?.name || ""}
        onInvited={load}
      />
      <div className="tg-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-3">User</th>
            <th className="text-left px-4 py-3">Role</th>
            <th className="text-left px-4 py-3">Position</th>
            <th className="text-left px-4 py-3">Supervisor</th>
            <th className="text-left px-4 py-3">Work start</th>
            <th className="text-left px-4 py-3">Employee #</th>
            <th className="text-right px-4 py-3 w-16">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filtered.map((u) => {
            const isMe = u.id && u.id === me?.id;
            const archived = u.membership_status === "archived";
            const pending = !!u.pending;
            const rowKey = u.id || u.membership_id || u.email;
            const editing = editingKey === rowKey;
            const supName = users.find((x) => x.id === u.supervisor_id)?.name;
            return (
            <tr key={rowKey} data-testid={`user-row-${rowKey}`} className={archived ? "bg-slate-50/50 text-slate-400" : ""}>
              <td className="px-4 py-3">
                <div className="font-semibold flex items-center gap-2">
                  {u.name || <span className="italic text-slate-400">Awaiting registration</span>}
                  {pending && <span className="tg-pill bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Pending</span>}
                  {archived && <span className="tg-pill bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Archived</span>}
                </div>
                <div className="text-xs text-slate-500">{u.email}</div>
              </td>
              <td className="px-4 py-3">
                {editing ? (
                  <select
                    value={draft.role}
                    onChange={(e) => setDraft((d) => ({ ...d, role: e.target.value }))}
                    className="tg-input max-w-[140px]"
                    data-testid={`role-select-${rowKey}`}
                  >
                    {["employee","supervisor","advisor","founder","admin"].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                ) : (
                  <span className="capitalize" data-testid={`role-text-${rowKey}`}>{u.role}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editing ? (
                  <select
                    value={draft.position}
                    onChange={(e) => setDraft((d) => ({ ...d, position: e.target.value }))}
                    className="tg-input max-w-[170px]"
                    data-testid={`position-select-${rowKey}`}
                  >
                    <option value="">— None —</option>
                    {positions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-slate-600" data-testid={`position-text-${rowKey}`}>{u.position || "— None —"}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editing ? (
                  <select
                    value={draft.supervisor_id}
                    onChange={(e) => setDraft((d) => ({ ...d, supervisor_id: e.target.value }))}
                    className="tg-input max-w-[200px]"
                    data-testid={`supervisor-select-${rowKey}`}
                  >
                    <option value="">— None —</option>
                    {users.filter((x) => x.id && x.id !== u.id && x.membership_status !== "archived" && !x.pending).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="text-slate-600" data-testid={`supervisor-text-${rowKey}`}>{supName || "— None —"}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editing ? (
                  <input
                    type="date"
                    value={draft.work_start_date}
                    onChange={(e) => setDraft((d) => ({ ...d, work_start_date: e.target.value }))}
                    className="tg-input max-w-[160px]"
                    data-testid={`start-date-${rowKey}`}
                  />
                ) : (
                  <span className="text-slate-600" data-testid={`start-date-text-${rowKey}`}>{u.work_start_date || "—"}</span>
                )}
              </td>
              <td className="px-4 py-3">
                {editing ? (
                  <input
                    value={draft.employee_id_number}
                    onChange={(e) => setDraft((d) => ({ ...d, employee_id_number: e.target.value }))}
                    placeholder="001"
                    className="tg-input max-w-[120px]"
                    data-testid={`emp-num-${rowKey}`}
                  />
                ) : (
                  <span className="font-mono text-slate-600" data-testid={`emp-num-text-${rowKey}`}>{u.employee_id_number || "—"}</span>
                )}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1.5">
                  {editing ? (
                    <>
                      <button onClick={() => saveEdit(u)} className="h-8 w-8 rounded-lg hover:bg-emerald-50 text-emerald-600 inline-flex items-center justify-center" data-testid={`user-save-${rowKey}`} title="Save">
                        <Check className="h-4 w-4" />
                      </button>
                      <button onClick={cancelEdit} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 inline-flex items-center justify-center" data-testid={`user-cancel-edit-${rowKey}`} title="Cancel">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : pending ? (
                    <button onClick={() => cancelInvite(u)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-flex items-center justify-center" data-testid={`user-cancel-${rowKey}`} title="Cancel invite">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : archived ? (
                    <>
                      <button onClick={() => unarchive(u)} className="h-8 w-8 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600 inline-flex items-center justify-center" data-testid={`user-unarchive-${rowKey}`} title="Restore">
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button onClick={() => del(u)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-flex items-center justify-center" data-testid={`user-delete-${rowKey}`} title="Remove">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => startEdit(u, rowKey)} className="h-8 w-8 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 inline-flex items-center justify-center" data-testid={`user-edit-${rowKey}`} title="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      {!isMe && (
                        <>
                          <button onClick={() => archive(u)} className="h-8 w-8 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 inline-flex items-center justify-center" data-testid={`user-archive-${rowKey}`} title="Archive">
                            <Archive className="h-4 w-4" />
                          </button>
                          <button onClick={() => del(u)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 inline-flex items-center justify-center" data-testid={`user-delete-${rowKey}`} title="Remove">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {isMe && <span className="text-[10px] text-slate-400">(you)</span>}
                    </>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function WorkTab() {
  const confirm = useConfirm();
  const fileInputRef = useRef(null);
  const [depts, setDepts] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [activeDeptId, setActiveDeptId] = useState("");
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const r = await api.get("/departments");
    setDepts(r.data || []);
    if ((r.data || []).length && !activeDeptId) setActiveDeptId(r.data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const cur = depts.find((d) => d.id === activeDeptId);

  const addDept = async () => {
    if (!newDept.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/departments", { name: newDept.trim() });
      setNewDept("");
      await load();
      setActiveDeptId(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const delDept = async (d) => {
    const ok = await confirm({
      title: `Archive ${d.name}?`,
      description: "Categories under it will also be archived.",
      confirmText: "Archive",
    });
    if (!ok) return;
    await api.delete(`/admin/departments/${d.id}`);
    if (activeDeptId === d.id) setActiveDeptId("");
    load();
  };

  const addCat = async () => {
    if (!newCat.trim() || !activeDeptId) return;
    setBusy(true);
    try {
      await api.post("/admin/categories", { department_id: activeDeptId, name: newCat.trim() });
      setNewCat("");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const delCat = async (c) => {
    const ok = await confirm({
      title: `Archive "${c.name}"?`,
      confirmText: "Archive",
    });
    if (!ok) return;
    await api.delete(`/admin/categories/${c.id}`);
    load();
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/dept-cat/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "departments-categories.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) { return; }
    const ok = await confirm({
      title: "Replace work categories?",
      description: "Importing makes this file the source of truth: departments and work categories not present in the file will be removed. This affects the whole workspace. Continue?",
      confirmText: "Replace from file",
    });
    if (!ok) { e.target.value = ""; return; }
    importXlsx(f).finally(() => { e.target.value = ""; });
  };

  const importXlsx = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/dept-cat/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.rows_read} rows · ${data.departments_created} new depts · ${data.categories_created} new categories`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="work-tab">
      {/* Excel import/export header */}
      <div className="tg-card p-4 mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="text-sm font-semibold text-slate-800">Bulk import / export</div>
          <div className="text-xs text-slate-500 mt-0.5">Two-column Excel: <span className="font-mono">Dept</span> | <span className="font-mono">Work Category</span></div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={onPickFile}
          data-testid="import-file-input"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="tg-btn-secondary disabled:opacity-50"
          data-testid="import-btn"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Import
        </button>
        <button onClick={exportXlsx} className="tg-btn-secondary" data-testid="export-btn">
          <Download className="h-4 w-4" /> Export
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Departments */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Departments</div>
          <div className="tg-card p-3 mb-2 flex gap-2">
            <input
              className="tg-input flex-1"
              placeholder="New department"
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addDept()}
              data-testid="new-dept-input"
            />
            <button onClick={addDept} disabled={busy || !newDept.trim()} className="tg-btn-primary disabled:opacity-50" data-testid="add-dept-btn">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="tg-card divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {depts.length === 0 && <div className="p-6 text-sm text-slate-400 text-center">No departments yet.</div>}
            {depts.map((d) => (
              <button
                key={d.id}
                onClick={() => setActiveDeptId(d.id)}
                className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${activeDeptId === d.id ? "bg-emerald-50/60" : "hover:bg-slate-50"}`}
                data-testid={`dept-row-${d.id}`}
              >
                <div className="flex-1">
                  <div className={`font-semibold ${activeDeptId === d.id ? "text-emerald-700" : "text-slate-800"}`}>{d.name}</div>
                  <div className="text-xs text-slate-500">{(d.categories || []).length} categories</div>
                </div>
                <span
                  onClick={(e) => { e.stopPropagation(); delDept(d); }}
                  className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center cursor-pointer"
                  data-testid={`del-dept-${d.id}`}
                  role="button"
                  tabIndex={0}
                >
                  <Trash2 className="h-4 w-4" />
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Categories of selected dept */}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            Work categories {cur && <span className="text-slate-700">· {cur.name}</span>}
          </div>
          <div className="tg-card p-3 mb-2 flex gap-2">
            <input
              className="tg-input flex-1"
              placeholder={cur ? `New category in ${cur.name}` : "Select a department first"}
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCat()}
              disabled={!cur}
              data-testid="new-cat-input"
            />
            <button onClick={addCat} disabled={busy || !newCat.trim() || !cur} className="tg-btn-primary disabled:opacity-50" data-testid="add-cat-btn">
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="tg-card divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {(cur?.categories || []).map((c) => (
              <div key={c.id} className="px-4 py-3 flex items-center gap-3" data-testid={`cat-row-${c.id}`}>
                <Tag className="h-4 w-4 text-emerald-500" />
                <div className="flex-1 font-medium text-slate-800">{c.name}</div>
                <button onClick={() => delCat(c)} className="h-8 w-8 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center" data-testid={`del-cat-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {cur && (cur.categories || []).length === 0 && (
              <div className="p-6 text-sm text-slate-400 text-center">No categories yet.</div>
            )}
            {!cur && (
              <div className="p-6 text-sm text-slate-400 text-center">Pick a department to manage its categories.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PeriodsTab() {
  const [settings, setSettings] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [anchor, setAnchor] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, p] = await Promise.all([api.get("/admin/settings"), api.get("/pay-periods")]);
    setSettings(s.data);
    setAnchor(s.data.anchor_date);
    setPeriods(p.data.periods);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/settings", { anchor_date: anchor });
      toast.success("Pay period anchor updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="tg-card p-5 mb-4">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
          Pay Period Anchor (Sunday — Period 1 begins)
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="date"
            value={anchor || ""}
            onChange={(e) => setAnchor(e.target.value)}
            className="tg-input max-w-[200px]"
            data-testid="anchor-date-input"
          />
          <button onClick={save} disabled={busy} className="tg-btn-primary disabled:opacity-50" data-testid="anchor-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
          <p className="text-xs text-slate-500 ml-2">All bi-weekly periods compute from this anchor.</p>
        </div>
      </div>

      <div className="tg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Period</th>
              <th className="text-left px-4 py-3">Begin (Sunday)</th>
              <th className="text-left px-4 py-3">End (Saturday)</th>
              <th className="text-left px-4 py-3">Payday (Friday)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {periods.map((p) => (
              <tr key={p.number} data-testid={`period-${p.number}`}>
                <td className="px-4 py-2 font-semibold">#{p.number}</td>
                <td className="px-4 py-2">{p.start}</td>
                <td className="px-4 py-2">{p.end}</td>
                <td className="px-4 py-2">{p.payday}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
